import { useEffect, useState } from "react";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { cn } from "../../lib/utils";

/**
 * Global top progress bar driven by the TanStack query cache. While requests
 * are in flight the bar trickles from the left toward (but never reaching) the
 * full width; when the last one settles it completes to 100% and fades out.
 * A short show delay hides flashes from instant responses.
 */
export function TopLoader() {
  const active = useIsFetching() + useIsMutating() > 0;
  const [phase, setPhase] = useState<"idle" | "running" | "stopping">("idle");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (active) {
      const show = setTimeout(() => setPhase("running"), 150);
      return () => clearTimeout(show);
    }
    setPhase(phase => (phase === "running" ? "stopping" : phase));
  }, [active]);

  useEffect(() => {
    if (phase === "running") {
      // Start at a visible width; re-entering mid-fade clamps the stale
      // near-complete width back into the trickle range.
      setProgress(current => (current < 10 ? 10 : Math.min(current, 60)));
      const trickle = setInterval(() => setProgress(current => current + (90 - current) * 0.09), 250);
      return () => clearInterval(trickle);
    }
    if (phase === "stopping") {
      setProgress(100);
      const hide = setTimeout(() => { setPhase("idle"); setProgress(0); }, 450);
      return () => clearTimeout(hide);
    }
  }, [phase]);

  if (phase === "idle") return null;
  return (
    <div aria-hidden="true" className={cn("pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 overflow-hidden", phase === "stopping" && "opacity-0 transition-opacity duration-300")}>
      <div className="top-loader-track" style={{ width: `${progress}%` }} />
    </div>
  );
}
